import { useState, useRef, useCallback, useEffect } from 'react';
import { usePulseSocket } from '../hooks/usePulseSocket';
import { useAnimationLoop } from '../hooks/useAnimationLoop';
import { useIsMobile } from '../hooks/useIsMobile';
import { useVelocity } from '../hooks/useVelocity';
import { useHaptic } from '../hooks/useHaptic';
import { TileMessage } from './Tile';
import { TickerList } from './TickerList';
import { MSG_LIFETIME, hexAlpha } from '../lib/energy';
import { useRainClick } from '../hooks/useRainClick';
import { COLORS } from '../lib/colors';
import { Share2, Check, Menu, Volume2, VolumeX, AArrowDown, AArrowUp, Copy, Code, ExternalLink, Home, Plus } from 'lucide-react';

interface PulseSpaceProps {
  spaceId: string;
}

export function PulseSpace({ spaceId }: PulseSpaceProps) {
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === '1';
  const now = useAnimationLoop();
  const pulse = usePulseSocket(spaceId);
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localBuffer, setLocalBuffer] = useState('');
  const [localLastActivity, setLocalLastActivity] = useState<number | null>(null);
  const [localMessages, setLocalMessages] = useState<TileMessage[]>([]);
  const [copied, setCopied] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pulseMenuOpen, setPulseMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedCopied, setEmbedCopied] = useState<'script' | 'iframe' | null>(null);
  const [textScale, setTextScale] = useState(() => {
    const saved = localStorage.getItem('pulse-text-scale');
    return saved ? parseFloat(saved) : 1;
  });

  const rain = useRainClick(pulse.users);
  const localId = pulse.myId || '_self';
  const velocities = useVelocity(pulse.users, localBuffer.length, localId);
  useHaptic(pulse.users);
  const [roomNameInput, setRoomNameInput] = useState('');

  const focusInput = useCallback(() => inputRef.current?.focus(), []);

  // Update page title when room name changes
  useEffect(() => {
    document.title = pulse.roomName ? `${pulse.roomName} — pulse` : 'pulse';
  }, [pulse.roomName]);

  const handleNameChange = useCallback((newName: string) => {
    localStorage.setItem('pulse-name', newName);
    pulse.sendName(newName);
    // Re-assert saved color so server doesn't auto-derive from new name
    const savedColor = localStorage.getItem('pulse-color');
    if (savedColor) pulse.sendColor(savedColor);
  }, [pulse.sendName, pulse.sendColor]);

  const handleColorChange = useCallback((color: string) => {
    localStorage.setItem('pulse-color', color);
    pulse.sendColor(color);
  }, [pulse.sendColor]);

  const openSettings = useCallback(() => {
    setSettingsName(pulse.myName);
    setSettingsOpen(true);
  }, [pulse.myName]);

  const closeSettings = useCallback(() => {
    const trimmed = settingsName.trim().slice(0, 32);
    if (trimmed && trimmed !== pulse.myName) {
      handleNameChange(trimmed);
    }
    setSettingsOpen(false);
  }, [settingsName, pulse.myName, handleNameChange]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setLocalBuffer(text);
    setLocalLastActivity(Date.now());
    pulse.sendBuffer(text);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newBuffer = localBuffer + ' · ';
    setLocalBuffer(newBuffer);
    setLocalLastActivity(Date.now());
    pulse.sendBuffer(newBuffer);
  };

  // Clean expired local messages
  const visibleLocalMessages = localMessages.filter(
    (m) => now - m.submittedAt < MSG_LIFETIME
  );
  if (visibleLocalMessages.length !== localMessages.length) {
    // Defer cleanup to avoid render-during-render
    setTimeout(() => setLocalMessages(visibleLocalMessages), 0);
  }

  const adjustScale = (delta: number) => {
    setTextScale((prev) => {
      const next = Math.round(Math.min(1.6, Math.max(0.8, prev + delta)) * 10) / 10;
      localStorage.setItem('pulse-text-scale', String(next));
      return next;
    });
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = window.location.href;
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const userCount = pulse.users.size + 1; // used in header aria-label

  // Build messages per user from global messages list
  const messagesByUser = new Map<string, TileMessage[]>();
  for (const msg of pulse.messages) {
    const list = messagesByUser.get(msg.clientId) || [];
    list.push({ id: msg.id, text: msg.text, submittedAt: msg.submittedAt });
    messagesByUser.set(msg.clientId, list);
  }

  return (
    <div
      className="pulse-screen"
      style={{
        background: 'linear-gradient(155deg, #07070d 0%, #0b0b16 35%, #080812 100%)',
        fontFamily: '"DM Sans", sans-serif',
        color: '#e8e4dc',
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 12px 12px',
        overflow: 'hidden',
      }}
      onClick={() => { focusInput(); setMenuOpen(false); setPulseMenuOpen(false); }}
    >
      {/* Ambient orbs */}
      <div
        style={{
          position: 'absolute',
          top: '12%',
          left: '3%',
          width: 550,
          height: 550,
          background: 'radial-gradient(circle, rgba(255,107,43,.022) 0%, transparent 60%)',
          pointerEvents: 'none',
          animation: 'drift 25s ease-in-out infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '8%',
          right: '3%',
          width: 450,
          height: 450,
          background: 'radial-gradient(circle, rgba(78,205,196,.018) 0%, transparent 60%)',
          pointerEvents: 'none',
          animation: 'drift2 30s ease-in-out infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '45%',
          left: '45%',
          width: 650,
          height: 650,
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(167,139,250,.012) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />

      {/* Header */}
      {isEmbed ? (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px 8px',
            marginBottom: 6,
            borderBottom: '1px solid rgba(255,255,255,.04)',
            flexShrink: 0,
            gap: 8,
          }}
        >
          {pulse.roomName ? (
            <span
              style={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 13,
                color: '#aaa',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {pulse.roomName}
            </span>
          ) : (
            <div style={{ flex: 1 }} />
          )}
          <a
            href={`${window.location.origin}/p/${spaceId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              color: '#4a4a4a',
              fontFamily: '"DM Sans", sans-serif',
              textDecoration: 'none',
              transition: 'color .2s',
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: pulse.connected ? '#4ecdc4' : '#f87171',
                display: 'inline-block',
              }}
            />
            {userCount} online
            <ExternalLink size={10} strokeWidth={2} style={{ color: '#555' }} />
          </a>
        </header>
      ) : (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 2px 10px',
            marginBottom: 8,
            borderBottom: '1px solid rgba(255,255,255,.04)',
            flexShrink: 0,
            gap: 10,
          }}
        >
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setPulseMenuOpen(v => !v); setMenuOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px',
                borderRadius: 6,
              }}
            >
              <span
                style={{
                  color: '#ff6b2b',
                  fontSize: 16,
                  animation: 'breathe 4s ease-in-out infinite',
                  lineHeight: 1,
                }}
              >
                ●
              </span>
              <span
                style={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 2.5,
                  color: '#e8e4dc',
                }}
              >
                pulse
              </span>
            </button>

            {pulseMenuOpen && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: 6,
                  background: 'rgba(15,15,24,.95)',
                  border: '1px solid rgba(255,255,255,.08)',
                  borderRadius: 8,
                  padding: '8px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  zIndex: 10,
                  minWidth: 140,
                }}
              >
                <a
                  href="/"
                  onClick={(e) => { e.preventDefault(); setPulseMenuOpen(false); window.location.href = '/'; }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 12,
                    color: '#888',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px 4px',
                    borderRadius: 4,
                    textDecoration: 'none',
                    transition: 'color .2s',
                  }}
                >
                  <Home size={14} strokeWidth={2} />
                  pulse home
                </a>
                <a
                  href="/new"
                  onClick={(e) => { e.preventDefault(); setPulseMenuOpen(false); window.location.href = '/new'; }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 12,
                    color: '#888',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px 4px',
                    borderRadius: 4,
                    textDecoration: 'none',
                    transition: 'color .2s',
                  }}
                >
                  <Plus size={14} strokeWidth={2} />
                  new pulse
                </a>
              </div>
            )}
          </div>

          {pulse.roomName && (
            <>
              <span style={{ color: '#333', fontSize: 12 }}>·</span>
              <span
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 12,
                  color: '#888',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: isMobile ? 100 : 200,
                }}
              >
                {pulse.roomName}
              </span>
            </>
          )}

          <span style={{ color: '#222', fontSize: 12 }}>·</span>

          {/* Presence dots — one per participant, breathing at staggered rates */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
            aria-label={`${userCount} people here`}
          >
            {pulse.connected ? (
              <>
                {/* Local user dot */}
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: pulse.myColor,
                    display: 'inline-block',
                    animation: 'breathe 3s ease-in-out infinite',
                  }}
                />
                {/* Remote user dots (cap at 7 visible) */}
                {Array.from(pulse.users.values()).slice(0, 7).map((user, i) => (
                  <span
                    key={user.clientId}
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: user.color,
                      display: 'inline-block',
                      animation: 'breathe 3s ease-in-out infinite',
                      animationDelay: `${(i + 1) * 0.4}s`,
                    }}
                  />
                ))}
                {pulse.users.size > 7 && (
                  <span style={{ fontSize: 10, color: '#444', fontFamily: '"DM Sans", sans-serif', marginLeft: 1 }}>
                    +{pulse.users.size - 7}
                  </span>
                )}
              </>
            ) : (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#f87171',
                  display: 'inline-block',
                }}
              />
            )}
          </div>

          <div style={{ flex: 1 }} />

          <button
            onClick={handleCopy}
            style={{
              lineHeight: 1,
              color: copied ? '#4ecdc4' : '#ff6b2b',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              transition: 'color .3s',
              display: 'flex',
              alignItems: 'center',
            }}
            title="copy invite link"
          >
            {copied ? <Check size={15} strokeWidth={2.5} /> : <Share2 size={15} strokeWidth={2} />}
          </button>

          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => { if (!v) setRoomNameInput(pulse.roomName); return !v; }); }}
              style={{
                lineHeight: 1,
                color: '#555',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Menu size={16} strokeWidth={2} />
            </button>

            {menuOpen && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 6,
                  background: 'rgba(15,15,24,.95)',
                  border: '1px solid rgba(255,255,255,.08)',
                  borderRadius: 8,
                  padding: '8px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  zIndex: 10,
                  minWidth: 120,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555', letterSpacing: 0.5 }}>
                    room name
                  </label>
                  <input
                    value={roomNameInput}
                    onChange={(e) => setRoomNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = roomNameInput.trim().slice(0, 64);
                        pulse.sendRoomName(val);
                        setMenuOpen(false);
                      }
                    }}
                    onBlur={() => {
                      const val = roomNameInput.trim().slice(0, 64);
                      if (val !== pulse.roomName) pulse.sendRoomName(val);
                    }}
                    placeholder="untitled"
                    maxLength={64}
                    style={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: 12,
                      color: '#e8e4dc',
                      background: 'rgba(255,255,255,.04)',
                      border: '1px solid rgba(255,255,255,.08)',
                      borderRadius: 4,
                      padding: '5px 8px',
                      outline: 'none',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <button
                  onClick={() => rain.toggle()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 12,
                    color: rain.enabled ? '#4ecdc4' : '#666',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 2px',
                    transition: 'color .3s',
                  }}
                >
                  {rain.enabled ? <Volume2 size={14} strokeWidth={2} /> : <VolumeX size={14} strokeWidth={2} />}
                  {rain.enabled ? 'sound on' : 'sound off'}
                </button>

                <button
                  onClick={() => { setEmbedOpen(true); setMenuOpen(false); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 12,
                    color: '#666',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 2px',
                    transition: 'color .3s',
                  }}
                >
                  <Code size={14} strokeWidth={2} />
                  embed
                </button>

                {isMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 2px' }}>
                    <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#666' }}>size</span>
                    {[
                      { icon: <AArrowDown size={14} strokeWidth={2} />, delta: -0.1 },
                      { icon: <AArrowUp size={14} strokeWidth={2} />, delta: 0.1 },
                    ].map(({ icon, delta }, i) => (
                      <button
                        key={i}
                        onClick={() => adjustScale(delta)}
                        style={{
                          lineHeight: 1,
                          color: '#666',
                          background: 'rgba(255,255,255,.04)',
                          border: '1px solid rgba(255,255,255,.06)',
                          borderRadius: 4,
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
      )}

      {/* Invite card when alone (not in embed mode) */}
      {pulse.users.size === 0 && pulse.connected && !isEmbed && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 14,
            padding: '24px 16px',
            margin: '8px 0',
            background: 'rgba(255,255,255,.02)',
            border: '1px solid rgba(255,255,255,.06)',
            borderRadius: 10,
            flexShrink: 0,
          }}
        >
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 13,
              color: '#888',
              margin: 0,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            words fade after 25 seconds. there's no scrollback. share this link and say something worth reading in time.
          </p>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 0,
              width: '100%',
              maxWidth: 360,
              background: 'rgba(0,0,0,.3)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            <input
              readOnly
              value={window.location.href}
              onClick={(e) => (e.target as HTMLInputElement).select()}
              style={{
                flex: 1,
                fontFamily: '"Space Mono", monospace',
                fontSize: 11,
                color: '#aaa',
                background: 'none',
                border: 'none',
                outline: 'none',
                padding: '8px 10px',
                minWidth: 0,
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard?.writeText(window.location.href);
                setInviteCopied(true);
                setTimeout(() => setInviteCopied(false), 2000);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 12,
                color: inviteCopied ? '#4ecdc4' : '#ff6b2b',
                background: inviteCopied ? 'rgba(78,205,196,.08)' : 'rgba(255,107,43,.08)',
                border: 'none',
                borderLeft: '1px solid rgba(255,255,255,.06)',
                cursor: 'pointer',
                padding: '8px 12px',
                transition: 'color .3s, background .3s',
                whiteSpace: 'nowrap',
              }}
            >
              {inviteCopied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2} />}
              {inviteCopied ? 'copied' : 'copy link'}
            </button>
          </div>

          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 11,
              color: '#555',
              margin: 0,
              textAlign: 'center',
            }}
          >
            drop it in Slack, iMessage, Discord — they'll see every keystroke live
          </p>
        </div>
      )}

      <TickerList
        users={[
          {
            id: localId,
            name: pulse.myName || 'you',
            color: pulse.myColor,
            buffer: localBuffer,
            messages: visibleLocalMessages,
            lastActivity: localLastActivity,
            velocity: velocities.get(localId) ?? 0,
          },
          ...Array.from(pulse.users.values()).map((user) => ({
            id: user.clientId,
            name: user.name,
            color: user.color,
            buffer: user.buffer,
            messages: messagesByUser.get(user.clientId) || [],
            lastActivity: user.lastActivity,
            velocity: velocities.get(user.clientId) ?? 0,
          })),
        ]}
        now={now}
        textScale={textScale}
        localId={localId}
        inputRef={inputRef}
        onInput={(text) => {
          setLocalBuffer(text);
          setLocalLastActivity(Date.now());
          pulse.sendBuffer(text);
        }}
        onSubmit={() => {
          const newBuffer = localBuffer + ' · ';
          setLocalBuffer(newBuffer);
          setLocalLastActivity(Date.now());
          pulse.sendBuffer(newBuffer);
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
              el.setSelectionRange(newBuffer.length, newBuffer.length);
              el.scrollLeft = el.scrollWidth;
            }
          });
        }}
        onBadgeTap={openSettings}
        isMobile={isMobile}
      />

      {/* Settings dialog */}
      {settingsOpen && (
        <div
          onClick={closeSettings}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(5,5,10,.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(18,18,28,.95)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 12,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              width: 260,
            }}
          >
            {/* Name */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555', letterSpacing: 0.5 }}>
                name
              </label>
              <input
                value={settingsName}
                onChange={(e) => setSettingsName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') closeSettings(); }}
                maxLength={32}
                autoFocus
                style={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: 14,
                  color: '#e8e4dc',
                  background: 'rgba(255,255,255,.04)',
                  border: `1px solid ${hexAlpha(pulse.myColor, 60)}`,
                  borderRadius: 6,
                  padding: '8px 10px',
                  outline: 'none',
                  caretColor: pulse.myColor,
                }}
              />
            </div>

            {/* Color palette */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555', letterSpacing: 0.5 }}>
                color
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => handleColorChange(c)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      backgroundColor: c,
                      border: c === pulse.myColor ? '2px solid #e8e4dc' : '2px solid transparent',
                      cursor: 'pointer',
                      padding: 0,
                      boxShadow: c === pulse.myColor ? `0 0 10px ${hexAlpha(c, 120)}` : 'none',
                      transition: 'border .15s, box-shadow .15s',
                    }}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={closeSettings}
              style={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 13,
                color: '#888',
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(255,255,255,.06)',
                borderRadius: 6,
                padding: '6px 0',
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              done
            </button>
          </div>
        </div>
      )}

      {/* Embed dialog */}
      {embedOpen && (
        <div
          onClick={() => setEmbedOpen(false)}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(5,5,10,.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(18,18,28,.95)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 12,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              width: 340,
              maxWidth: '90vw',
            }}
          >
            <span
              style={{
                fontFamily: '"Space Mono", monospace',
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: '#e8e4dc',
              }}
            >
              embed
            </span>

            {/* Script tag snippet */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555', letterSpacing: 0.5 }}>
                floating widget
              </label>
              <div style={{ position: 'relative' }}>
                <pre
                  style={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 10,
                    color: '#aaa',
                    background: 'rgba(0,0,0,.3)',
                    border: '1px solid rgba(255,255,255,.06)',
                    borderRadius: 6,
                    padding: '10px 12px',
                    paddingRight: 50,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {`<script src="${window.location.origin}/embed.js" data-room="${spaceId}"></script>`}
                </pre>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      `<script src="${window.location.origin}/embed.js" data-room="${spaceId}"></script>`
                    );
                    setEmbedCopied('script');
                    setTimeout(() => setEmbedCopied(null), 2000);
                  }}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    background: 'rgba(255,255,255,.06)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: 4,
                    padding: '3px 6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: embedCopied === 'script' ? '#4ecdc4' : '#666',
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 10,
                    transition: 'color .3s',
                  }}
                >
                  {embedCopied === 'script' ? <Check size={11} strokeWidth={2.5} /> : <Copy size={11} strokeWidth={2} />}
                  {embedCopied === 'script' ? 'copied' : 'copy'}
                </button>
              </div>
            </div>

            {/* Iframe snippet */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555', letterSpacing: 0.5 }}>
                inline iframe
              </label>
              <div style={{ position: 'relative' }}>
                <pre
                  style={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 10,
                    color: '#aaa',
                    background: 'rgba(0,0,0,.3)',
                    border: '1px solid rgba(255,255,255,.06)',
                    borderRadius: 6,
                    padding: '10px 12px',
                    paddingRight: 50,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {`<iframe src="${window.location.origin}/p/${spaceId}?embed=1" width="400" height="600" style="border:none;border-radius:12px" allow="clipboard-write"></iframe>`}
                </pre>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      `<iframe src="${window.location.origin}/p/${spaceId}?embed=1" width="400" height="600" style="border:none;border-radius:12px" allow="clipboard-write"></iframe>`
                    );
                    setEmbedCopied('iframe');
                    setTimeout(() => setEmbedCopied(null), 2000);
                  }}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    background: 'rgba(255,255,255,.06)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: 4,
                    padding: '3px 6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: embedCopied === 'iframe' ? '#4ecdc4' : '#666',
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 10,
                    transition: 'color .3s',
                  }}
                >
                  {embedCopied === 'iframe' ? <Check size={11} strokeWidth={2.5} /> : <Copy size={11} strokeWidth={2} />}
                  {embedCopied === 'iframe' ? 'copied' : 'copy'}
                </button>
              </div>
            </div>

            <button
              onClick={() => setEmbedOpen(false)}
              style={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 13,
                color: '#888',
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(255,255,255,.06)',
                borderRadius: 6,
                padding: '6px 0',
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
